/**
 * telephonyAudioService.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Resilient multi-engine telephony audio manager with proactive user-gesture priming.
 *
 * Problem Solved:
 * - Exotel SDK's own ringtone fails in Vite due to require() resolving to ES module object.
 * - Browsers block autoplay unless unlocked via user gesture (click/keydown).
 * - Minimized tabs throttle JS timers (setTimeout), but hardware HTML5 <audio> continues.
 *
 * Engine Hierarchy:
 * 1. Primary:   HTML5 <audio> playing '/ringtone.wav' (served statically from public/)
 * 2. Secondary: HTML5 <audio> playing synthesized Base64 WAV data URI (in-memory)
 * 3. Fallback:  Web Audio API dual-oscillator (400Hz + 450Hz) telephone bell cadence
 *
 * Priming:
 * - primeAudio() is called automatically on the very first user interaction (click/keydown)
 *   or explicitly when toggling SIP ON or clicking "Test Sound".
 */

// ── Synthesized WAV tone (Dual-tone 400Hz+450Hz telephone cadence, 8kHz mono) ──────────
function _buildSineWaveDataURI(durationSecs = 2.0, sampleRate = 8000) {
  const numSamples = Math.floor(sampleRate * durationSecs)
  const byteCount = 44 + numSamples * 2
  const buffer = new ArrayBuffer(byteCount)
  const view = new DataView(buffer)

  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
  }
  writeStr(0, 'RIFF')
  view.setUint32(4, byteCount - 8, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeStr(36, 'data')
  view.setUint32(40, numSamples * 2, true)

  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate
    // 0.0s - 0.4s: Ring 1, 0.4s - 0.6s: Gap, 0.6s - 1.0s: Ring 2, 1.0s - 2.0s: Pause
    const inRing = (t >= 0.0 && t < 0.4) || (t >= 0.6 && t < 1.0)
    let sample = 0
    if (inRing) {
      const s1 = Math.sin(2 * Math.PI * 400 * t)
      const s2 = Math.sin(2 * Math.PI * 450 * t)
      sample = (s1 + s2) * 0.35
    }
    view.setInt16(44 + i * 2, Math.round(sample * 32767), true)
  }

  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
  return 'data:audio/wav;base64,' + btoa(binary)
}

// ─────────────────────────────────────────────────────────────────────────────

class TelephonyAudioService {
  constructor() {
    this._audioContext = null
    this._audioElement = null
    this._dataAudioElement = null
    this._synthTimer = null
    this._testTimer = null
    this._isPrimed = false
    this._isRinging = false
    this._isTestMode = false
    this._toneDataURI = null
  }

  isPrimed() {
    return this._isPrimed
  }

  /**
   * 1. Prime: unlock browser autoplay policy on user gesture (click/keydown).
   * Unlocks both Web Audio API AudioContext and HTML5 <audio> elements.
   */
  primeAudio() {
    if (this._isPrimed) return
    try {
      // 1. Prime Web Audio API
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (AudioCtx) {
        if (!this._audioContext) {
          this._audioContext = new AudioCtx()
        }
        if (this._audioContext.state === 'suspended') {
          this._audioContext.resume().catch(() => {})
        }
        const silentBuffer = this._audioContext.createBuffer(1, 1, 22050)
        const src = this._audioContext.createBufferSource()
        src.buffer = silentBuffer
        src.connect(this._audioContext.destination)
        src.start(0)
      }

      // 2. Pre-create and warm up primary HTML5 <audio> with static /ringtone.wav
      if (!this._audioElement) {
        const audio = new Audio('/ringtone.wav')
        audio.loop = true
        audio.volume = 0.85
        audio.preload = 'auto'
        this._audioElement = audio
      }

      // Quick 0-volume play/pause cycle during user gesture permanently unlocks HTML5 audio in Chrome
      this._audioElement.volume = 0.01
      const p = this._audioElement.play()
      if (p !== undefined) {
        p.then(() => {
          if (!this._isRinging) {
            this._audioElement.pause()
            this._audioElement.currentTime = 0
            this._audioElement.volume = 0.85
          }
          console.log('[TelephonyAudio] HTML5 audio primed and unlocked')
        }).catch((err) => {
          console.warn('[TelephonyAudio] HTML5 prime warm-up notice:', err)
          this._audioElement.volume = 0.85
        })
      }

      this._isPrimed = true
      console.log('[TelephonyAudio] AudioContext & HTML5 audio primed')
    } catch (e) {
      console.warn('[TelephonyAudio] primeAudio error:', e)
    }
  }

  /**
   * Test Ringtone: Play ringtone for 2.5 seconds to verify speakers and prime audio.
   */
  testRingtone(durationMs = 2500) {
    console.log('[TelephonyAudio] Testing ringtone')
    this.primeAudio()
    this._isTestMode = true
    this.startRingtone()

    if (this._testTimer) clearTimeout(this._testTimer)
    this._testTimer = setTimeout(() => {
      if (this._isTestMode) {
        this.stopRingtone()
        this._isTestMode = false
      }
    }, durationMs)
  }

  /**
   * 2. startRingtone: Multi-engine playback
   */
  startRingtone() {
    if (this._isRinging) return
    this._isRinging = true
    console.log('[TelephonyAudio] Starting ringtone')

    // Always attempt priming if not yet primed
    if (!this._isPrimed) {
      this.primeAudio()
    }

    // Attempt 1: Primary HTML5 Audio with static file /ringtone.wav
    const started = this._startHTML5Ringtone()
    if (!started) {
      console.log('[TelephonyAudio] Primary HTML5 audio failed synchronously — trying secondary in-memory Data-URI')
      this._startDataURIRingtone()
    }
  }

  // ── Engine 1: Primary HTML5 Audio (/ringtone.wav) ──────────────────────────
  _startHTML5Ringtone() {
    try {
      if (!this._audioElement) {
        const audio = new Audio('/ringtone.wav')
        audio.loop = true
        audio.volume = 0.85
        audio.preload = 'auto'
        this._audioElement = audio
      }

      this._audioElement.volume = 0.85
      this._audioElement.currentTime = 0

      const playPromise = this._audioElement.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('[TelephonyAudio] HTML5 /ringtone.wav playing successfully')
          })
          .catch((err) => {
            console.warn('[TelephonyAudio] HTML5 /ringtone.wav play() rejected:', err, '— trying in-memory Data-URI')
            this._stopHTML5Ringtone()
            this._startDataURIRingtone()
          })
      }
      return true
    } catch (e) {
      console.warn('[TelephonyAudio] HTML5 ringtone error:', e)
      return false
    }
  }

  _stopHTML5Ringtone() {
    try {
      if (this._audioElement) {
        this._audioElement.pause()
        this._audioElement.currentTime = 0
      }
    } catch (_) {}
  }

  // ── Engine 2: Secondary In-Memory Base64 WAV Data-URI ──────────────────────
  _startDataURIRingtone() {
    try {
      if (!this._toneDataURI) {
        this._toneDataURI = _buildSineWaveDataURI(2.0, 8000)
      }
      if (!this._dataAudioElement) {
        const dataAudio = new Audio(this._toneDataURI)
        dataAudio.loop = true
        dataAudio.volume = 0.8
        this._dataAudioElement = dataAudio
      }

      this._dataAudioElement.currentTime = 0
      const playPromise = this._dataAudioElement.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('[TelephonyAudio] In-memory Data-URI ringtone playing')
          })
          .catch((err) => {
            console.warn('[TelephonyAudio] Data-URI play rejected:', err, '— falling back to Web Audio oscillator')
            this._stopDataURIRingtone()
            this._startOscillatorRingtone()
          })
      }
    } catch (e) {
      console.warn('[TelephonyAudio] Data-URI start error:', e)
      this._startOscillatorRingtone()
    }
  }

  _stopDataURIRingtone() {
    try {
      if (this._dataAudioElement) {
        this._dataAudioElement.pause()
        this._dataAudioElement.currentTime = 0
        this._dataAudioElement = null
      }
    } catch (_) {}
  }

  // ── Engine 3: Fallback Web Audio API Oscillator (Dual 400Hz + 450Hz) ────────
  _startOscillatorRingtone() {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) return

      if (!this._audioContext) {
        this._audioContext = new AudioCtx()
      }
      if (this._audioContext.state === 'suspended') {
        this._audioContext.resume().catch(() => {})
      }

      const ringCycle = () => {
        if (!this._isRinging) return
        this._playOscillatorBurst(400, 450, 0.4, 0.45) // 1st ring
        setTimeout(() => {
          if (!this._isRinging) return
          this._playOscillatorBurst(400, 450, 0.4, 0.45) // 2nd ring
          this._synthTimer = setTimeout(ringCycle, 2000) // 2s pause, then repeat
        }, 600)
      }
      ringCycle()
      console.log('[TelephonyAudio] Web Audio API oscillator ring started')
    } catch (e) {
      console.warn('[TelephonyAudio] Oscillator fallback error:', e)
    }
  }

  _playOscillatorBurst(freq1, freq2, durationSecs, volume = 0.4) {
    try {
      const ctx = this._audioContext
      if (!ctx || ctx.state === 'suspended') return

      const gain = ctx.createGain()
      gain.gain.setValueAtTime(volume, ctx.currentTime)
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + durationSecs)
      gain.connect(ctx.destination)

      const osc1 = ctx.createOscillator()
      osc1.type = 'sine'
      osc1.frequency.value = freq1
      osc1.connect(gain)
      osc1.start(ctx.currentTime)
      osc1.stop(ctx.currentTime + durationSecs)

      const osc2 = ctx.createOscillator()
      osc2.type = 'sine'
      osc2.frequency.value = freq2
      osc2.connect(gain)
      osc2.start(ctx.currentTime)
      osc2.stop(ctx.currentTime + durationSecs)
    } catch (_) {}
  }

  _stopOscillatorRingtone() {
    if (this._synthTimer) {
      clearTimeout(this._synthTimer)
      this._synthTimer = null
    }
  }

  // ── 3. stopRingtone: Stop all engines ──────────────────────────────────────
  stopRingtone() {
    if (this._testTimer) {
      clearTimeout(this._testTimer)
      this._testTimer = null
    }
    this._isRinging = false
    this._isTestMode = false
    console.log('[TelephonyAudio] Ringtone stopped')
    this._stopHTML5Ringtone()
    this._stopDataURIRingtone()
    this._stopOscillatorRingtone()
  }
}

// Singleton — one audio engine per tab
const telephonyAudioService = new TelephonyAudioService()
export default telephonyAudioService
